import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Zap, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setPendingInvite, consumePendingInvite } from "@/lib/invite";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Join() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/team/invites/${token}/preview`);
        setInvite(data);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "This invitation is invalid.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post(`/team/invites/${token}/accept`);
      toast.success(`You've joined ${invite?.business_name}!`);
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setAccepting(false);
    }
  };

  const goAuth = (path) => {
    setPendingInvite(token);
    navigate(path);
  };

  const loggedIn = user && user !== false;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-12">
      <Link to="/" className="mb-8 flex items-center gap-2.5" data-testid="join-brand">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary"><Zap className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} /></div>
        <span className="font-display text-lg font-bold tracking-tight">BizForge<span className="text-accent">AI</span></span>
      </Link>

      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center animate-fade-up" data-testid="join-card">
        {loading || authLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
        ) : error ? (
          <>
            <p className="font-display text-xl font-bold" data-testid="join-error">Invitation unavailable</p>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <Button asChild className="mt-6 w-full"><Link to="/">Back to home</Link></Button>
          </>
        ) : (
          <>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/15"><Users className="h-6 w-6 text-accent" /></div>
            <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">You've been invited</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Join <span className="font-semibold text-foreground">{invite?.business_name}</span> on BizForge AI
              {invite?.invited_by ? <> — invited by {invite.invited_by}</> : null}.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="text-xs text-muted-foreground">Role</span>
              <Badge variant="secondary" className="capitalize" data-testid="join-role">{invite?.role}</Badge>
            </div>

            {loggedIn ? (
              <Button className="mt-6 w-full" onClick={accept} disabled={accepting} data-testid="join-accept-button">
                {accepting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
                Accept &amp; join as {user?.name?.split(" ")[0] || "you"}
              </Button>
            ) : (
              <div className="mt-6 space-y-2.5">
                <Button className="w-full" onClick={() => goAuth("/register")} data-testid="join-signup-button">
                  Create an account to join
                </Button>
                <Button variant="outline" className="w-full" onClick={() => goAuth("/login")} data-testid="join-login-button">
                  I already have an account
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
